import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, Search, Edit, Save, X, Users,
  TrendingUp, Percent, Euro, RefreshCw, CheckCircle, AlertCircle,
  FileSignature, Download, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';
import { toast } from 'sonner';

const AdminPartnerSettingsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPartner, setEditingPartner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Commission levels configuration
  const commissionLevels = [
    { level: 1, name: 'Bronze', rate: 15, minCases: 0 },
    { level: 2, name: 'Silber', rate: 18, minCases: 10 },
    { level: 3, name: 'Gold', rate: 22, minCases: 25 },
    { level: 4, name: 'Platin', rate: 25, minCases: 50 },
    { level: 5, name: 'Diamant', rate: 30, minCases: 100 },
  ];

  // Safe function to get commission rate
  const getCommissionRate = (partner) => {
    if (partner.custom_commission_rate) return partner.custom_commission_rate;
    if (partner.provision_level) return partner.provision_level;
    const level = commissionLevels.find(l => l.level === (partner.commission_level || 1));
    return level ? level.rate : 15;
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadPartners();
  }, [token, navigate]);

  const loadPartners = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/partners`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setPartners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePartnerSettings = async (partnerId, updates) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/partners/${partnerId}/settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updates)
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Einstellungen gespeichert' });
        setEditingPartner(null);
        loadPartners();
      } else {
        setMessage({ type: 'error', text: 'Fehler beim Speichern' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Verbindungsfehler' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const filteredPartners = partners.filter(p => 
    p.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLevelColor = (level) => {
    const colors = {
      1: 'bg-amber-700',
      2: 'bg-gray-400',
      3: 'bg-yellow-500',
      4: 'bg-blue-400',
      5: 'bg-purple-500'
    };
    return colors[level] || 'bg-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-xl font-bold text-white">Partner-Einstellungen</h1>
          </div>
          <span className="text-white/50 text-xs md:text-sm hidden sm:block">{getVersionString()}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {message.text}
            </span>
          </div>
        )}

        {/* Commission Levels Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Provisions-Stufen</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {commissionLevels.map(level => (
              <div key={level.level} className="text-center p-3 bg-gray-50 rounded-lg">
                <div className={`w-8 h-8 ${getLevelColor(level.level)} rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-sm`}>
                  {level.level}
                </div>
                <div className="font-semibold text-gray-900">{level.name}</div>
                <div className="text-brand-green font-bold">{level.rate}%</div>
                <div className="text-xs text-gray-500">ab {level.minCases} Fälle</div>
              </div>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Partner suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
            />
          </div>
        </div>

        {/* Partners List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900">Partner ({filteredPartners.length})</h2>
          </div>
          
          <div className="divide-y divide-gray-100">
            {filteredPartners.filter(p => p && p.id).map(partner => (
              <div key={partner.id} className="p-4 hover:bg-gray-50">
                {editingPartner?.id === partner.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">{partner.company_name || partner.email || 'Partner'}</h3>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingPartner(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-brand-green hover:bg-brand-green-dark text-white"
                          onClick={() => savePartnerSettings(partner.id, editingPartner)}
                          disabled={saving}
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Speichern
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Provisions-Level
                        </label>
                        <select
                          value={editingPartner.commission_level || 1}
                          onChange={(e) => setEditingPartner({
                            ...editingPartner,
                            commission_level: parseInt(e.target.value)
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          {commissionLevels.map(level => (
                            <option key={level.level} value={level.level}>
                              Level {level.level} - {level.name} ({level.rate}%)
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Individuelle Provision (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          value={editingPartner.custom_commission_rate || ''}
                          onChange={(e) => setEditingPartner({
                            ...editingPartner,
                            custom_commission_rate: e.target.value ? parseFloat(e.target.value) : null
                          })}
                          placeholder="Standard verwenden"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          value={editingPartner.status || 'active'}
                          onChange={(e) => setEditingPartner({
                            ...editingPartner,
                            status: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="active">Aktiv</option>
                          <option value="trial">Trial</option>
                          <option value="suspended">Gesperrt</option>
                          <option value="inactive">Inaktiv</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 ${getLevelColor(partner.commission_level || 1)} rounded-full flex items-center justify-center text-white font-bold`}>
                        {partner.commission_level || 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{partner.company_name || partner.email}</h3>
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-gray-600">
                          <span className="truncate max-w-[180px]">{partner.email}</span>
                          <span className="flex items-center gap-1">
                            <Percent className="w-4 h-4" />
                            {getCommissionRate(partner)}%
                          </span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-4 h-4" />
                            {partner.total_cases || 0} Fälle
                          </span>
                          {/* Agreement Status */}
                          {partner.agreement_signed ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <FileSignature className="w-4 h-4" />
                              <span className="hidden md:inline">Vereinbarung</span>
                              <CheckCircle className="w-3.5 h-3.5" />
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-orange-500">
                              <FileSignature className="w-4 h-4" />
                              <span className="hidden md:inline">Vereinbarung</span>
                              <Clock className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Agreement Download Button */}
                      {partner.agreement_signed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            window.open(
                              `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/download/${partner.id}`,
                              '_blank'
                            );
                          }}
                          className="text-brand-green border-brand-green hover:bg-brand-green/5"
                          title="Vereinbarung herunterladen"
                          data-testid={`download-agreement-${partner.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        partner.subscription?.plan === 'trial' ? 'bg-amber-100 text-amber-700' :
                        partner.status === 'active' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {partner.subscription?.plan === 'trial' ? 'Trial' : partner.status || 'Aktiv'}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingPartner({
                          id: partner.id,
                          commission_level: partner.commission_level || 1,
                          custom_commission_rate: partner.custom_commission_rate,
                          status: partner.status || 'active'
                        })}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPartnerSettingsPage;
